import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ConvertFromRicosDocumentOptions,
  ConvertFromRicosDocumentResponse,
  ConvertToRicosDocumentOptions,
  ConvertToRicosDocumentResponse,
  RichContent,
  ValidateDocumentOptions,
  ValidateDocumentResponse,
  convertFromRicosDocument as universalConvertFromRicosDocument,
  convertToRicosDocument as universalConvertToRicosDocument,
  validateDocument as universalValidateDocument,
} from './ricos-v1-service-services.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ricos-services' };

/** @internal */
export function validateDocument(
  httpClient: HttpClient
): ValidateDocumentSignature {
  return (document: Record<string, any>, options?: ValidateDocumentOptions) =>
    universalValidateDocument(
      document,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ValidateDocumentSignature {
  /**
   * Validates a Ricos document against a set of rules and optionally fixes any violations
   * This method checks if the provided document structure conforms to the Ricos format
   * and can optionally attempt to fix any issues found
   * @param - Potential ricos document to validate
   */
  (document: Record<string, any>, options?: ValidateDocumentOptions): Promise<
    NonNullablePaths<
      ValidateDocumentResponse,
      `valid` | `violations` | `violations.${number}.message`,
      4
    >
  >;
}

/** @internal */
export function convertToRicosDocument(
  httpClient: HttpClient
): ConvertToRicosDocumentSignature {
  return (options?: ConvertToRicosDocumentOptions) =>
    universalConvertToRicosDocument(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ConvertToRicosDocumentSignature {
  /**
   * Convert content from various formats (Markdown, HTML, Plain text) to Ricos document format
   */
  (
    options?: ConvertToRicosDocumentOptions
  ): Promise<ConvertToRicosDocumentResponse>;
}

/** @internal */
export function convertFromRicosDocument(
  httpClient: HttpClient
): ConvertFromRicosDocumentSignature {
  return (
    document: RichContent,
    options?: NonNullablePaths<
      ConvertFromRicosDocumentOptions,
      `targetFormat`,
      2
    >
  ) =>
    universalConvertFromRicosDocument(
      document,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ConvertFromRicosDocumentSignature {
  /**
   * Convert content from Ricos document format to various formats (Markdown, HTML, Plain text)
   * @param - Ricos document to be converted
   */
  (
    document: RichContent,
    options?: NonNullablePaths<
      ConvertFromRicosDocumentOptions,
      `targetFormat`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ConvertFromRicosDocumentResponse,
      `markdown` | `html` | `plainText`,
      2
    >
  >;
}

export {
  Alignment,
  AnchorData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  AudioData,
  Background,
  BackgroundBackgroundOneOf,
  BackgroundType,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BulletedListData,
  ButtonData,
  CaptionData,
  CellStyle,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ConvertFromRicosDocumentOptions,
  ConvertFromRicosDocumentOptionsOptionsOneOf,
  ConvertFromRicosDocumentRequest,
  ConvertFromRicosDocumentRequestOptionsOneOf,
  ConvertFromRicosDocumentResponse,
  ConvertFromRicosDocumentResponseResultOneOf,
  ConvertToRicosDocumentOptions,
  ConvertToRicosDocumentOptionsSourceOneOf,
  ConvertToRicosDocumentRequest,
  ConvertToRicosDocumentRequestSourceOneOf,
  ConvertToRicosDocumentResponse,
  Crop,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  Design,
  Dimensions,
  Direction,
  DividerData,
  DocumentStyle,
  EmbedData,
  EventData,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontSizeData,
  FontType,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  Gradient,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  Image,
  ImageData,
  ImageDataStyles,
  InitialExpandedItems,
  Item,
  ItemDataOneOf,
  ItemStyle,
  Layout,
  LayoutCellData,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListValue,
  MapData,
  MapSettings,
  MapType,
  Media,
  MentionData,
  Metadata,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Oembed,
  Option,
  OptionDesign,
  OptionLayout,
  OrderedListData,
  Orientation,
  PDFSettings,
  ParagraphData,
  Permissions,
  PlainTextOptions,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  Position,
  Rel,
  RichContent,
  RicosDocumentOptions,
  RicosDocumentViolation,
  RicosService,
  Settings,
  Source,
  Spoiler,
  SpoilerData,
  Styles,
  StylesBorder,
  TableCellData,
  TableData,
  Target,
  TargetFormat,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  Type,
  ValidateDocumentOptions,
  ValidateDocumentRequest,
  ValidateDocumentResponse,
  VerticalAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VoteRole,
  Width,
  WidthType,
} from './ricos-v1-service-services.universal.js';
